import React, { createContext, useState, useCallback, useMemo } from "react";
import { fetchRoles, createRole, deleteRole } from "../services/roleService";
import {
  getRolePermissionDetails,
  updateRolePermissions,
} from "../services/assignmentService";

export const RoleContext = createContext();

export const RoleProvider = ({ children }) => {
  const [roles, setRoles] = useState([]);
  const [currentRole, setCurrentRole] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false); // For add/edit actions
  const [error, setError] = useState("");

  const clearError = useCallback(() => setError(""), []);

  // Load roles from the server
  const loadRoles = useCallback(
    async (token) => {
      setLoading(true);
      clearError();
      try {
        const response = await fetchRoles(token);
        // console.log(response.roles)
        setRoles(response.roles || []); // Set roles from API response
      } catch (error) {
        setError("Failed to fetch roles. Please try again.");
        console.error("Error loading roles:", error.message);
      } finally {
        setLoading(false);
      }
    },
    [clearError],
  );

  // Fetch details of a specific role (with permission list)
  const fetchRoleDetails = useCallback(
    async (token, roleId) => {
      clearError();
      try {
        const role = await getRolePermissionDetails(token, roleId);
        setCurrentRole(role);
      } catch (error) {
        setError("Failed to fetch role details.");
        console.error("Error fetching role details:", error);
        throw error;
      }
    },
    [clearError],
  );

  // Add a new role
  const addRole = useCallback(
    async (token, roleData) => {
      clearError();
      setActionLoading(true);
      try {
        const response = await createRole(token, roleData);
        await loadRoles(token);
        return { success: true, message: "Role created successfully." };
      } catch (error) {
        // console.log("error:", error)
        setError(error.message || "Failed to add role.");
        return {
          success: false,
          message: error.message,
          fieldErrors: error.fieldErrors,
        };
      } finally {
        setActionLoading(false);
      }
    },
    [clearError, loadRoles],
  );

  // Edit an existing role
  const editRole = useCallback(
    async (token, roleId, roleData) => {
      clearError();
      setActionLoading(true);
      try {
        const response = await updateRolePermissions(token, roleId, roleData);
        await loadRoles(token);
        return { success: true, message: response.message };
      } catch (error) {
        console.error("Error updating role:", error.response?.data?.message);
        setError(error.response?.data?.message || "Failed to edit role.");
        return { success: false, message: error.response?.data?.message };
      } finally {
        setActionLoading(false);
      }
    },
    [clearError, loadRoles],
  );

  // delete an existing role
  const deleteARole = useCallback(
    async (token, roleId) => {
      clearError();
      try {
        await deleteRole(token, roleId);
        setRoles((prevRoles) => prevRoles.filter((role) => role.id !== roleId));
        return { success: true };
      } catch (error) {
        console.error("Error deleting role:", error.response?.data?.message);
        setError(error.response?.data?.message || "Failed to delete role.");
        return { success: false, message: error.response?.data?.message };
      }
    },
    [clearError],
  );

  // Memoize the context value
  const value = useMemo(
    () => ({
      roles,
      currentRole,
      loading,
      actionLoading,
      error,
      loadRoles,
      fetchRoleDetails,
      addRole,
      editRole,
      deleteARole,
      clearError,
    }),
    [
      roles,
      currentRole,
      loading,
      actionLoading,
      error,
      loadRoles,
      fetchRoleDetails,
      addRole,
      editRole,
      deleteARole,
      clearError,
    ],
  );

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
};
